import type { ReactNode } from 'react';
import type { ThemeModePalette } from '@/utils/themeDerivation';

interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

function ColorField({ label, value, onChange }: ColorFieldProps) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-9 shrink-0 cursor-pointer rounded-md border border-border bg-bgElevated p-0.5"
        title={label}
      />
      <div className="min-w-0 flex-1">
        <label className="block text-xs text-textSecondary truncate">{label}</label>
        <input
          type="text"
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) onChange(v);
          }}
          className="mt-0.5 w-full rounded border border-border bg-bgElevated px-2 py-1 text-xs font-mono text-textDefault"
          maxLength={7}
        />
      </div>
    </div>
  );
}

interface ColorSectionProps {
  title: string;
  children: ReactNode;
}

function ColorSection({ title, children }: ColorSectionProps) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-textSecondary mb-3">{title}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">{children}</div>
    </div>
  );
}

interface ThemeColorEditorProps {
  palette: ThemeModePalette;
  onChange: (field: keyof ThemeModePalette, value: string) => void;
  t: (key: string) => string;
}

export function ThemeColorEditor({ palette, onChange, t }: ThemeColorEditorProps) {
  return (
    <div className="space-y-6">
      <ColorSection title={t('themeSettings.sectionPrimary')}>
        <ColorField label={t('themeSettings.primary')} value={palette.primary} onChange={(v) => onChange('primary', v)} />
        <ColorField label={t('themeSettings.secondary')} value={palette.secondary} onChange={(v) => onChange('secondary', v)} />
      </ColorSection>

      <ColorSection title={t('themeSettings.sectionBackground')}>
        <ColorField label={t('themeSettings.bgPage')} value={palette.bgPage} onChange={(v) => onChange('bgPage', v)} />
        <ColorField label={t('themeSettings.bgContainer')} value={palette.bgContainer} onChange={(v) => onChange('bgContainer', v)} />
        <ColorField label={t('themeSettings.bgElevated')} value={palette.bgElevated} onChange={(v) => onChange('bgElevated', v)} />
      </ColorSection>

      <ColorSection title={t('themeSettings.sectionText')}>
        <ColorField label={t('themeSettings.textDefault')} value={palette.textDefault} onChange={(v) => onChange('textDefault', v)} />
        <ColorField label={t('themeSettings.textPrimary')} value={palette.textPrimary} onChange={(v) => onChange('textPrimary', v)} />
        <ColorField label={t('themeSettings.textSecondary')} value={palette.textSecondary} onChange={(v) => onChange('textSecondary', v)} />
        <ColorField label={t('themeSettings.textTertiary')} value={palette.textTertiary} onChange={(v) => onChange('textTertiary', v)} />
        <ColorField label={t('themeSettings.textDisabled')} value={palette.textDisabled} onChange={(v) => onChange('textDisabled', v)} />
        <ColorField label={t('themeSettings.textDanger')} value={palette.textDanger} onChange={(v) => onChange('textDanger', v)} />
        <ColorField label={t('themeSettings.textLight')} value={palette.textLight} onChange={(v) => onChange('textLight', v)} />
      </ColorSection>
    </div>
  );
}
